import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Chip from 'tio-ui/components/chip';
import { eq } from 'tio-ui/utilities';
import { PhClock, PhWarningCircle, PhCheckCircle, PhXCircle } from 'ember-phosphor-icons';
import {
  APPLICATION_STATUS_MAP,
  PROGRAM_INSTANCE_STATUS_MAP,
  DEFAULT_STATUS,
  type StatusInfo,
} from 'tio-employee/utils/tas/applicationStateMap';
import type { IntlService } from 'ember-intl';
import type { RecentApplication } from 'tio-common/utils/tuition-assistance/application-states';

export interface ApplicationRowSignature {
  Args: {
    application: RecentApplication;
    isNested?: boolean; // When nested inside ProgramInstanceCard
  };
  Element: HTMLDivElement;
}

export default class ApplicationRow extends Component<ApplicationRowSignature> {
  @service declare intl: IntlService;

  get statusInfo(): StatusInfo {
    const app = this.args.application;

    // If there's a refund amount due, show "In Repayment" regardless of state
    if (app.paymentRefundRequestedAmount && app.paymentRefundRequestedAmount > 0) {
      return { label: 'In Repayment', intent: 'danger', icon: 'warning' };
    }

    // Check application state first, then fall back to program instance state
    const applicationState = app.tasApplicationStateKeyword;
    const programInstanceState = app.tasProgramInstanceStateKeyword;

    if (applicationState && APPLICATION_STATUS_MAP[applicationState]) {
      return APPLICATION_STATUS_MAP[applicationState];
    }

    if (programInstanceState && PROGRAM_INSTANCE_STATUS_MAP[programInstanceState]) {
      return PROGRAM_INSTANCE_STATUS_MAP[programInstanceState];
    }

    return DEFAULT_STATUS;
  }

  get applicationId() {
    return this.args.application.tasApplicationIdKeyword || this.args.application.id;
  }

  get applicationName() {
    const app = this.args.application;
    const type = app.tasProgramTemplateTypeKeyword;

    // For Type 2 or 3, check application name field first, then specialization
    if (type === 'TAS.ProgramType.2' || type === 'TAS.ProgramType.3') {
      return (
        app.tasApplicationFieldApplicationNameKeyword ||
        app.tasProgramInstanceFieldProgramSpecializationKeyword ||
        `Application ID# ${this.applicationId}`
      );
    }

    // For other types (Type 4.x child apps), use application name or fall back to ID
    return app.tasApplicationFieldApplicationNameKeyword || `Application ID# ${this.applicationId}`;
  }

  get programName() {
    return this.args.application.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program';
  }

  get formattedCreatedDate() {
    const dateStr = this.args.application.tasProgramInstanceCreatedAtDate;
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const formatted = this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    return `created ${formatted}`;
  }

  get formattedUpdatedDate() {
    const dateStr = this.args.application.updatedAt;
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const formatted = this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    return `updated ${formatted}`;
  }

  get hasPaymentAmount() {
    const app = this.args.application;
    return (
      (app.paymentPaidAmount ?? 0) > 0 ||
      (app.paymentPendingAmount ?? 0) > 0 ||
      (app.paymentFinalApprovedTotalAmount ?? 0) > 0 ||
      (app.paymentRefundRequestedAmount ?? 0) > 0 ||
      (app.tasApplicationFieldPreApprovedTotalInteger ?? 0) > 0 ||
      (app.tasApplicationFieldFinalApprovedTotalInteger ?? 0) > 0 ||
      (app.tasApplicationFieldCardPreApprovedTotalInteger ?? 0) > 0 ||
      (app.tasApplicationFieldCardFinalApprovedTotalInteger ?? 0) > 0
    );
  }

  /**
   * Determine payment status type for display
   */
  get paymentStatus() {
    const app = this.args.application;

    // Check program instance state - DRAFT/PRIMED means program approved but courses not yet approved
    const instanceState = app.tasProgramInstanceStateKeyword;
    const applicationState = app.tasApplicationStateKeyword;
    const isRequestedState =
      instanceState === 'TAS.ProgramInstanceState.DRAFT' ||
      instanceState === 'TAS.ProgramInstanceState.PRIMED' ||
      applicationState === 'TAS.ApplicationState.CREATED' ||
      applicationState === 'TAS.ApplicationState.DEFINE_COURSES';

    // Check if there's a refund amount due (in repayment) - show as "due now" in red
    if (app.paymentRefundRequestedAmount && app.paymentRefundRequestedAmount > 0) {
      return {
        amount: app.paymentRefundRequestedAmount,
        label: 'due now',
        colorClass: 'tio-fg-danger-body',
      };
    }

    // Paid - show in green with date
    if (app.paymentPaidAmount && app.paymentPaidAmount > 0) {
      return {
        amount: app.paymentPaidAmount,
        label: this.formattedPaidDate,
        colorClass: 'tio-fg-info-body',
      };
    }

    // Pending payment
    if (app.paymentPendingAmount && app.paymentPendingAmount > 0) {
      return { amount: app.paymentPendingAmount, label: 'pending', colorClass: 'tio-fg-disabled' };
    }

    // Type 4 application amounts (includes tuition + course cash) - CHECK FIRST
    // Use PRE_APPROVED for requested states, FINAL_APPROVED for approved states
    const tuitionAmount = isRequestedState
      ? (app.tasApplicationFieldPreApprovedTotalInteger ?? 0)
      : (app.tasApplicationFieldFinalApprovedTotalInteger ??
        app.tasApplicationFieldPreApprovedTotalInteger ??
        0);
    const courseCashAmount = isRequestedState
      ? (app.tasApplicationFieldCardPreApprovedTotalInteger ?? 0)
      : (app.tasApplicationFieldCardFinalApprovedTotalInteger ??
        app.tasApplicationFieldCardPreApprovedTotalInteger ??
        0);
    const totalAmount = tuitionAmount + courseCashAmount;

    if (totalAmount > 0) {
      return {
        amount: totalAmount,
        label: isRequestedState ? 'requested' : 'approved',
        colorClass: 'tio-fg-disabled',
      };
    }

    // Legacy payment fields (for non-Type 4 applications)
    if (app.paymentFinalApprovedTotalAmount && app.paymentFinalApprovedTotalAmount > 0) {
      return {
        amount: app.paymentFinalApprovedTotalAmount,
        label: 'requested',
        colorClass: 'tio-fg-disabled',
      };
    }

    return { amount: 0, label: '', colorClass: 'tio-fg-disabled' };
  }

  get formattedPaidDate() {
    // TODO: Add payment date field when available from OpenSearch
    // For now, use updated_at as a proxy
    const dateStr = this.args.application.updatedAt;
    if (!dateStr) return 'paid';

    const date = new Date(dateStr);
    return this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  }

  /**
   * Format amount - amounts from OpenSearch are in pennies
   */
  get formattedAmount() {
    const amount = this.paymentStatus.amount;
    return `$${(amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }

  get iconBackgroundClass(): string {
    switch (this.statusInfo.icon) {
      case 'check':
        return 'tio-bg-info-soft';
      case 'warning':
        return 'tio-bg-danger-soft';
      case 'x':
        return 'tio-bg-chip';
      default:
        return 'tio-bg-chip';
    }
  }

  <template>
    <div
      class="p-4 tio-row-loose {{unless @isNested 'border tio-border-default rounded-xl'}}"
      ...attributes
    >
      {{! Left icon indicator }}
      <div class="shrink-0 p-0.5 rounded-full {{this.iconBackgroundClass}}">
        {{#if (eq this.statusInfo.icon "check")}}
          <PhCheckCircle class="tio-icon-md tio-fg-info" />
        {{else if (eq this.statusInfo.icon "warning")}}
          <PhWarningCircle class="tio-icon-md tio-fg-danger" />
        {{else if (eq this.statusInfo.icon "x")}}
          <PhXCircle class="tio-icon-md tio-fg-muted" />
        {{else}}
          <PhClock class="tio-icon-md tio-fg-muted" />
        {{/if}}
      </div>

      {{! Middle - Application info }}
      <div class="tio-fill-min flex flex-col gap-1.5">
        {{! Row 1: Application Name + Status Chip }}
        <div class="tio-row-tight">
          <LinkTo
            @route="authenticated.tas.applications.show"
            @model={{this.applicationId}}
            class="font-medium tio-fg-primary hover:tio-fg-info hover:underline"
          >
            {{this.applicationName}}
          </LinkTo>
          <Chip @intent={{this.statusInfo.intent}} @appearance="faded" @size="sm">
            {{this.statusInfo.label}}
          </Chip>
        </div>

        {{! Row 2: Program name • created date • updated date (hide program name when nested) }}
        <div class="flex items-center gap-1.5 text-sm tio-fg-disabled tracking-wide">
          {{#unless @isNested}}
            <span>{{this.programName}}</span>
            <span class="tio-fg-faint">•</span>
          {{/unless}}
          <span>{{this.formattedCreatedDate}}</span>
          {{#if this.formattedUpdatedDate}}
            <span class="tio-fg-faint">•</span>
            <span>{{this.formattedUpdatedDate}}</span>
          {{/if}}
        </div>
      </div>

      {{! Right side - Amount }}
      <div class="shrink-0 text-right min-w-20">
        {{#if this.hasPaymentAmount}}
          <div class="font-semibold {{this.paymentStatus.colorClass}}">
            {{this.formattedAmount}}
          </div>
          <div class="text-xs tio-fg-disabled tracking-wide">
            {{this.paymentStatus.label}}
          </div>
        {{else}}
          <div class="font-semibold tio-fg-disabled">
            ---
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
